import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { FundSetup } from '../../data/types';
import { canPublish, sectionValid } from '../../domain/setup/setupRules';
import { OFFER_FORM_SECTIONS, sectionById } from '../../domain/setup/sections';
import { date, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { ConfirmDialog, useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard } from '../../ui/primitives';

export default function ReviewPublishPanel({
  setup,
  onGoTo,
}: {
  setup: FundSetup;
  onGoTo: (sectionId: string) => void;
}) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const { values } = setup;
  const value = (key: string) => String(values[key] ?? '');
  const invalid = OFFER_FORM_SECTIONS.filter((id) => !sectionValid(id, values));
  const published = setup.status === 'PUBLISHED';
  const methods = [values['offer.payEft'] && 'EFT', values['offer.payBpay'] && 'BPAY'].filter(
    Boolean,
  );

  return (
    <>
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        Review & publish
      </Typography>
      {invalid.map((id) => (
        <Alert
          key={id}
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => onGoTo(id)}>
              Fix
            </Button>
          }
        >
          {sectionById(id)?.title} needs attention.
        </Alert>
      ))}
      <Stack spacing={2}>
        <SectionCard title="Offer">
          <KeyValueGrid
            items={[
              { label: 'Title', value: value('offer.title') },
              {
                label: 'Unit class',
                value: `${value('class.name')} at $${value('class.issuePrice')}`,
              },
              { label: 'Offer link', value: value('offer.url') },
              {
                label: 'Applications close',
                value: `${date(value('offer.closeDate'))}, ${value('offer.closeTime')} (${value('offer.timezone')})`,
              },
              {
                label: 'Documents',
                value: value('offer.documents').split('\n').filter(Boolean).join(', '),
              },
              {
                label: 'Existing investors',
                value: values['offer.useExisting'] ? 'Shown in the investor centre' : 'Hidden',
              },
              {
                label: 'New investors',
                value: values['offer.publicApply']
                  ? 'Can apply from the public link'
                  : 'Brought on by the Fides team',
              },
            ]}
          />
        </SectionCard>
        <SectionCard title="Investment & payment">
          <KeyValueGrid
            items={[
              { label: 'Minimum', value: money(Number(value('offer.min'))) },
              {
                label: 'Maximum',
                value: value('offer.max') ? money(Number(value('offer.max'))) : 'No maximum',
              },
              { label: 'Increment', value: money(Number(value('offer.increment'))) },
              {
                label: 'Payable now',
                value:
                  Number(value('offer.payableNow')) < Number(value('class.issuePrice'))
                    ? `$${value('offer.payableNow')} of $${value('class.issuePrice')} per unit (partly paid)`
                    : `$${value('offer.payableNow')} per unit (fully paid)`,
              },
              {
                label: 'Payment',
                value: `${methods.join(' or ')}, ${
                  values['offer.payDueMode'] === 'DATE'
                    ? `due by ${date(value('offer.payDate'))}`
                    : `due within ${value('offer.payDays')} days`
                }`,
              },
              { label: 'Alerts to', value: value('notify.newApplication') },
            ]}
          />
        </SectionCard>
      </Stack>
      {published ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            The offer is live.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={`/investor/offers/${setup.offerId}`}
            >
              See it as an investor
            </Button>
            <Button size="small" variant="outlined" component={RouterLink} to="/manager/offers">
              Offers dashboard
            </Button>
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/manager/applications"
            >
              Applications
            </Button>
          </Stack>
        </Alert>
      ) : setup.publishRequestedAt ? (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button component={RouterLink} to="/manager/approvals" size="small">
              Approvals
            </Button>
          }
        >
          Sent by {setup.publishRequestedBy} for a second person to publish. In the demo, switch{' '}
          <strong>Signed in as</strong> to Priya Nair and approve it.
        </Alert>
      ) : (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 3 }}
        >
          <Typography variant="caption" color="text.secondary">
            A second person publishes, so every offer gets a fresh pair of eyes.
          </Typography>
          <Button
            variant="contained"
            disabled={!canPublish(setup)}
            onClick={() => setConfirming(true)}
          >
            Send for approval to publish
          </Button>
        </Box>
      )}
      <ConfirmDialog
        open={confirming}
        title="Send this offer for approval?"
        body={`Once a second person approves, ${value('class.name')} in ${value('fund.name')} opens to applications and existing Fides investors see it in their investor centre.`}
        confirmLabel="Send for approval"
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          dispatch({ type: 'SETUP_REQUEST_PUBLISH', id: setup.id });
          toast('Sent to Approvals');
          setConfirming(false);
        }}
      />
    </>
  );
}
